import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import ProcessSteps from '../components/ProcessSteps';
import UploadPreview from '../components/UploadPreview';
import UploadCard from '../components/UploadCard';
import CostSummary from '../components/CostSummary';
import InvoicingStep from './InvoicingStep';
import PhotoGuideStep from './PhotoGuideStep'; // Import our enhanced PhotoGuideStep component
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';
import i18n from '../i18n';

// Helper function to get localized value from a multilingual field object
const getLocalizedValue = (obj, lang = i18n.language) => {
  if (!obj) return '';
  if (typeof obj !== 'object') return obj;
  if (obj[lang]) return obj[lang];
  
  // Fallback to first available language
  const firstLang = Object.keys(obj)[0];
  return obj[firstLang] || '';
};

// Helper to generate a unique ID
const generateUniqueId = () => `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Remove the old simplified PhotoGuide component since we're using the enhanced PhotoGuideStep

const UploadProcess = () => {
  const { t } = useTranslation('upload');
  const [currentStep, setCurrentStep] = useState(1);
  const [uploads, setUploads] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bucketId, setBucketId] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [imageThemes, setImageThemes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showingPhotoGuide, setShowingPhotoGuide] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Load prompts and suggestions on mount
  useEffect(() => {
    const fetchPromptsAndSuggestions = async () => {
      try {
        const [promptsResponse, suggestionsResponse] = await Promise.all([
          api.get('/api/content/prompts'),
          api.get('/api/content/suggestion-pictograms')
        ]);
        
        setPrompts(promptsResponse.data.prompts);
        setSuggestions(suggestionsResponse.data.suggestions || []);
        
        // Get unique image themes
        const uniqueThemesSet = new Set();
        promptsResponse.data.prompts.forEach(p => {
          // Always get the localized value for the theme
          const localizedTheme = getLocalizedValue(p.imageTheme);
          if (localizedTheme) {
            uniqueThemesSet.add(localizedTheme);
          }
        });
        
        // Convert Set to array of strings
        const uniqueThemes = Array.from(uniqueThemesSet);
        setImageThemes(uniqueThemes);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchPromptsAndSuggestions();
  }, []);

  // Load stored uploads on mount
  useEffect(() => {
    const loadStoredUploads = async () => {
      const storedUploads = sessionStorage.getItem('uploads');
      const existingBucketId = sessionStorage.getItem('existingBucketId');
      
      let parsedUploads = [];
      if (storedUploads) {
        try {
          parsedUploads = JSON.parse(storedUploads);
          if (Array.isArray(parsedUploads) && parsedUploads.length > 0) {
            // The preview URLs may be outdated secure URLs or direct paths
            // In both cases, we'll keep them as-is initially, and refresh later if needed
            setUploads(parsedUploads.map(upload => ({
              ...upload,
              id: upload.id || generateUniqueId(),
              preview: upload.preview || upload.filename
            })));
          }
        } catch (error) {
          console.error('Error loading stored uploads:', error);
        }
      }

      if (existingBucketId) {
        setBucketId(existingBucketId);
        
        // For existing buckets, refresh the content data to get the latest secure URLs
        if (parsedUploads?.length > 0) {
          try {
            const response = await api.get(`/api/content/bucket/${existingBucketId}`);
            if (response.data.contents?.length > 0) {
              // Create a map of contentId -> photoUrl for easy lookup
              const contentUrlMap = {};
              response.data.contents.forEach(content => {
                contentUrlMap[content.id] = content.photoUrl;
              });
              
              // Update the previews with fresh secure URLs
              const refreshedUploads = parsedUploads.map(upload => {
                if (upload.contentId && contentUrlMap[upload.contentId]) {
                  return {
                    ...upload,
                    preview: contentUrlMap[upload.contentId]
                  };
                }
                return upload;
              });
              
              setUploads(refreshedUploads);
              sessionStorage.setItem('uploads', JSON.stringify(refreshedUploads));
            }
          } catch (error) {
            console.error('Error refreshing bucket content:', error);
          }
        }
      }
    };

    loadStoredUploads();
  }, []);

  // Add file compression utility
  const compressFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions (max 1200px width/height)
          const maxSize = 1200;
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with reduced quality
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }));
            },
            'image/jpeg',
            0.7 // Reduced quality
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    // Validate file types
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(f => f.name).join(', ');
      alert(t('errors.invalidFileType', { files: invalidFileNames }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      const formData = new FormData();
      
      // Map files and create basic upload objects
      const mappedFiles = files.map(file => ({
        id: generateUniqueId(),
        imageTheme: '', // Start with empty theme
        actions: [], // Start with no actions until theme is selected
        filename: file.name
      }));

      // Process and append files to formData
      for (const file of files) {
        let fileToUpload = file;
        if (file.size > 1024 * 1024) { // If larger than 1MB
          fileToUpload = await compressFile(file);
        }
        formData.append('files', fileToUpload);
      }

      // Find default prompt for initial content entries
      let defaultPromptId = null;
      if (prompts.length > 0) {
        defaultPromptId = prompts[0].id;
      } else {
        // Wait for prompts to load (maximum 5 seconds)
        for (let i = 0; i < 10; i++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          if (prompts.length > 0) {
            defaultPromptId = prompts[0].id;
            break;
          }
        }
      }

      if (!defaultPromptId) {
        throw new Error('No prompts available. Please try again in a few moments.');
      }
      
      // Create content entries
      const contentEntries = mappedFiles.map(() => ({
        actions: [{
          promptId: defaultPromptId,
          variety: 1
        }]
      }));

      formData.append('contentEntries', JSON.stringify(contentEntries));
      
      if (bucketId) {
        formData.append('bucketId', bucketId);
      }

      // Upload files to backend
      const uploadResponse = await api.post('/api/content/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      // If this is the first upload, store the new bucketId
      if (!bucketId) {
        setBucketId(uploadResponse.data.bucketId);
        sessionStorage.setItem('existingBucketId', uploadResponse.data.bucketId);
      }

      // Update the mapped files with content IDs and server URLs from the response
      // The backend will now provide secure URLs for the photos
      const updatedMappedFiles = mappedFiles.map((file, index) => ({
        ...file,
        contentId: uploadResponse.data.content[index].id,
        preview: uploadResponse.data.content[index].photoUrl // Use the secure URL directly
      }));

      const newUploads = [...uploads, ...updatedMappedFiles];
      setUploads(newUploads);

      // Store minimal data in sessionStorage
      const minimalData = newUploads.map(({ id, imageTheme, actions, filename, contentId, preview }) => ({
        id, imageTheme, actions, filename, contentId, preview
      }));
      sessionStorage.setItem('uploads', JSON.stringify(minimalData));

    } catch (error) {
      console.error('Error processing files:', error);
      alert(error.message || 'Failed to upload one or more files. Please try again.');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageThemeChange = (uploadId, newTheme) => {
    const newUploads = uploads.map(upload => {
      if (upload.id === uploadId) {
        let actions = [];
        if (newTheme) {
          const themePrompts = prompts.filter(p => 
            getLocalizedValue(p.imageTheme) === newTheme
          );

          if (themePrompts.length > 0) {
            actions = [{
              promptId: themePrompts[0].id,
              variety: 1
            }];
          }
        }

        return {
          ...upload,
          imageTheme: newTheme,
          actions
        };
      }
      return upload;
    });

    setUploads(newUploads);

    // Update session storage
    const minimalData = newUploads.map(({ id, imageTheme, actions, filename, contentId, preview }) => ({
      id, imageTheme, actions, filename, contentId, preview
    }));
    sessionStorage.setItem('uploads', JSON.stringify(minimalData));
  };

  const handleAddAction = (uploadId) => {
    const newUploads = uploads.map(upload => {
      if (upload.id === uploadId) {
        const selectedPromptIds = upload.actions.map(a => a.promptId);

        // Filter prompts by theme and exclude already selected ones
        const availablePrompts = prompts.filter(p =>
          getLocalizedValue(p.imageTheme) === upload.imageTheme &&
          !selectedPromptIds.includes(p.id)
        );

        if (availablePrompts.length > 0) {
          return {
            ...upload,
            actions: [
              ...upload.actions,
              {
                promptId: availablePrompts[0].id,
                variety: 1
              }
            ]
          };
        }
      }
      return upload;
    });

    setUploads(newUploads);
    
    // Update session storage with minimal data
    const minimalData = newUploads.map(({ id, imageTheme, actions, filename, contentId, preview }) => ({
      id, imageTheme, actions, filename, contentId, preview
    }));
    sessionStorage.setItem('uploads', JSON.stringify(minimalData));
  };

  const handleRemoveAction = (uploadId, actionIndex) => {
    const newUploads = uploads.map(upload => {
      if (upload.id === uploadId) {
        const newActions = [...upload.actions];
        newActions.splice(actionIndex, 1);

        return {
          ...upload,
          actions: newActions
        };
      }
      return upload;
    });

    setUploads(newUploads);
  };

  const handleActionChange = (uploadId, actionIndex, newPromptId) => {
    const newUploads = uploads.map(upload => {
      if (upload.id === uploadId) {
        const newActions = [...upload.actions];
        newActions[actionIndex] = {
          ...newActions[actionIndex],
          promptId: newPromptId
        };

        return {
          ...upload,
          actions: newActions
        };
      }
      return upload;
    });

    setUploads(newUploads);
  };

  const handleVarietyChange = (uploadId, actionIndex, value) => {
    const newUploads = uploads.map(upload => {
      if (upload.id === uploadId) {
        const newActions = [...upload.actions];
        newActions[actionIndex] = {
          ...newActions[actionIndex],
          variety: parseInt(value) || 1
        };

        return {
          ...upload,
          actions: newActions
        };
      }
      return upload;
    });

    setUploads(newUploads);
  };

  const handleRemoveUpload = async (uploadId) => {
    const uploadToRemove = uploads.find(upload => upload.id === uploadId);
    if (!uploadToRemove) return;

    const newUploads = uploads.filter(upload => upload.id !== uploadId);
    setUploads(newUploads);

    const minimalData = newUploads.map(({ id, imageTheme, actions, filename, contentId, preview }) => ({
      id, imageTheme, actions, filename, contentId, preview
    }));
    sessionStorage.setItem('uploads', JSON.stringify(minimalData));

    // Only handle backend file deletion if we have a contentId
    if (uploadToRemove.contentId) {
      try {
        // Extract the filename from the secure URL
        let filename;
        if (uploadToRemove.preview.includes('/api/files/access')) {
          // For secure URLs, parse the path parameter
          const urlParams = new URLSearchParams(uploadToRemove.preview.split('?')[1]);
          filename = urlParams.get('path');
        } else if (uploadToRemove.preview.includes('/uploads/')) {
          // For direct paths (legacy)
          filename = uploadToRemove.preview.split('/uploads/')[1];
        } else {
          filename = uploadToRemove.preview;
        }
        
        const response = await api.delete(`/api/content/file/${encodeURIComponent(filename)}`);

        if (response.data.wasLastContent) {
          navigate('/dashboard');
          return;
        }
      } catch (error) {
        console.error('Error deleting file:', error.message);
      }
    }

    // Only navigate to dashboard if we're editing existing content and there are no uploads left
    if (bucketId && newUploads.length === 0) {
      navigate('/dashboard');
    }
  };

  const hasInvalidUploads = () => {
    return uploads.some(upload => {
      return !upload.imageTheme || upload.imageTheme === '' || !upload.actions || upload.actions.length === 0;
    });
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      if (uploads.length === 0 || !consentChecked || hasInvalidUploads()) {
        return; // Early return if no uploads, consent not checked, or invalid uploads
      }

      try {
        // Update all content with their current actions
        const contentUpdates = uploads.map(upload => ({
          id: upload.contentId,
          actions: upload.actions
        }));

        // Send content updates
        await api.post(`/api/content/update-batch`, {
          contents: contentUpdates
        });
        
        // Fetch last invoice data before moving to step 2
        try {
          const lastInvoiceResponse = await api.get('/api/invoice/last');
          if (lastInvoiceResponse.data.invoice) {
            const invoiceData = JSON.stringify(lastInvoiceResponse.data);
            sessionStorage.setItem('lastInvoiceData', invoiceData);
          }
        } catch (error) {
          console.error('Error fetching last invoice:', error);
        }
        
        setCurrentStep(2);

      } catch (error) {
        console.error('Failed to update content:', error);
        alert('Failed to save changes. Please try again or contact support if the issue persists.');
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleCreatePaymentSession = async (invoiceData) => {
    try {
      const response = await api.post('/api/payment/create-checkout-session', {
        bucketId,
        invoiceData
      });

      if (response.data?.url) {
        sessionStorage.removeItem('uploads');
        sessionStorage.removeItem('existingBucketId');
        window.location.href = response.data.url;
      } else {
        alert(t('errors.paymentCreationFailed'));
      }
    } catch (error) {
      alert(error.response?.data?.error || t('errors.paymentCreationFailed'));
    }
  };

  return (
    <div>
      <Navbar />
      <div className="container mx-auto p-4 mt-16">
        <ProcessSteps currentStep={currentStep} />
        {currentStep === 1 && !showingPhotoGuide && (
          <div>
            <h2 className={theme.text.h2 + ' mb-4'}>
              {bucketId ? t('titles.editContent') : t('titles.selectPhotos')}
            </h2>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full md:w-3/4">
                <div className="flex items-center gap-4 mb-4">
                  <button
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    className={`${theme.button.base} ${theme.button.primary} ${theme.button.sizes.md}`}
                  >
                    {bucketId ? t('buttons.addMore') : t('buttons.selectPhotos')}
                  </button>
                  <button
                    onClick={() => setShowingPhotoGuide(true)}
                    className={`${theme.button.base} ${theme.button.secondary} ${theme.button.sizes.md} flex items-center gap-2`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('titles.howToChoose')}
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="flex flex-wrap gap-4">
                  {uploads.map((upload) => (
                    <UploadCard
                      key={upload.id}
                      upload={upload}
                      getPreview={() => upload.preview}
                      imageThemes={imageThemes}
                      prompts={prompts}
                      suggestions={suggestions}
                      onImageThemeChange={(newTheme) => handleImageThemeChange(upload.id, newTheme)}
                      onAddAction={() => handleAddAction(upload.id)}
                      onRemoveAction={(actionIndex) => handleRemoveAction(upload.id, actionIndex)}
                      onActionChange={(actionIndex, newPromptId) =>
                        handleActionChange(upload.id, actionIndex, newPromptId)}
                      onVarietyChange={(actionIndex, value) =>
                        handleVarietyChange(upload.id, actionIndex, value)}
                      onDelete={() => handleRemoveUpload(upload.id)}
                    />
                  ))}
                </div>
              </div>
              <div className="w-full md:w-1/4">
                <div className="sticky top-20">
                  <CostSummary uploads={uploads} />
                  
                  {uploads.length > 0 && (
                    <>
                      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 text-blue-600"
                            checked={consentChecked}
                            onChange={(e) => setConsentChecked(e.target.checked)}
                          />
                          <span className="ml-2 text-sm text-gray-600">
                            {t('consent.text')} {' '}
                            <span 
                              className="text-blue-600 cursor-pointer hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowingPhotoGuide(true);
                              }}
                            >
                              {t('consent.guideLink')}
                            </span>
                          </span>
                        </label>
                      </div>
                      
                      <button
                        onClick={handleNextStep}
                        className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.lg} w-full mt-4 text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl ${(!consentChecked || hasInvalidUploads()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={uploads.length === 0 || !consentChecked || hasInvalidUploads()}
                      >
                        {t('buttons.nextInvoicing')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {currentStep === 1 && showingPhotoGuide && (
          <div className="w-full">
            <PhotoGuideStep backUrl="/process" onBack={() => setShowingPhotoGuide(false)} />
          </div>
        )}
        {currentStep === 2 && (
          <div>
            <InvoicingStep
              onComplete={async (invoiceData) => {
                try {
                  await handleCreatePaymentSession(invoiceData);
                } catch (error) {
                  console.error('Error in payment flow:', error);
                }
              }}
              bucketId={bucketId}
            />
          </div>
        )}
        {currentStep === 3 && (
          <div className="text-center">
            <h2 className={theme.text.h2 + ' mb-4'}>{t('steps.paymentComplete')}</h2>
            <p className={theme.text.body}>{t('messages.paymentSuccess')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadProcess;